<template>
  <div class="bg-white rounded-lg shadow p-5">
    <!-- En-tête + santé -->
    <div class="flex flex-wrap items-center justify-between gap-2 mb-3">
      <h3 class="font-semibold text-gray-800">{{ mode === 'intraday' ? $t('batterie.titreHoraire') : $t('batterie.titreQuotidien') }}</h3>
      <div class="flex flex-wrap gap-2">
        <span v-if="healthBadge"
              :class="`px-3 py-1 rounded-full text-sm font-semibold ${healthBadge.cls}`"
              :title="healthBadge.detail">
          {{ healthBadge.icon }} {{ healthBadge.label }}
        </span>
        <!-- Bilan de charge (signe de dq) : second avis INDÉPENDANT du rebond bat_mv -->
        <span v-if="balanceBadge"
              :class="`px-3 py-1 rounded-full text-sm font-semibold ${balanceBadge.cls}`"
              :title="balanceBadge.detail">
          {{ balanceBadge.icon }} {{ balanceBadge.label }}
        </span>
        <!-- Dérive de conso : la carte contre SA PROPRE base, jamais contre les autres -->
        <span v-if="driftBadge"
              :class="`px-3 py-1 rounded-full text-sm font-semibold ${driftBadge.cls}`"
              :title="driftBadge.detail">
          {{ driftBadge.icon }} {{ driftBadge.label }}
        </span>
      </div>
    </div>
    <p v-if="healthBadge && healthBadge.detail" class="text-xs text-gray-500 mb-3">{{ healthBadge.detail }}</p>
    <p v-if="balanceBadge && balanceBadge.detail" class="text-xs text-gray-500 mb-3">{{ balanceBadge.detail }}</p>
    <p v-if="driftBadge && driftBadge.detail" class="text-xs text-gray-500 mb-3">{{ driftBadge.detail }}</p>
    <!-- Réserve : pente de `cap` (fw >= 66350). Rien à afficher sur le parc ancien. -->
    <p v-if="consumptionLine" class="text-xs text-gray-600 mb-1">{{ consumptionLine }}</p>
    <i18n-t v-if="consumptionLine" keypath="batterie.aideConso" tag="p" class="text-xs text-gray-400 mb-3">
      <template #cette><em>{{ $t('batterie.cette') }}</em></template>
    </i18n-t>

    <!-- Contrôles : zoom (plage) + navigation -->
    <div class="flex flex-wrap items-center gap-2 mb-3">
      <div class="inline-flex rounded-md overflow-hidden border border-gray-200">
        <button v-for="r in ranges" :key="r.d" @click="setRange(r.d)"
                :class="['px-3 py-1 text-sm', rangeDays === r.d ? 'bg-primary-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50']">
          {{ r.label }}
        </button>
      </div>
      <div class="inline-flex items-center gap-1 ml-1">
        <button @click="shift(-1)" class="px-2 py-1 text-sm rounded border border-gray-200 bg-white text-gray-600 hover:bg-gray-50">←</button>
        <button @click="shift(1)" :disabled="!canForward"
                :class="['px-2 py-1 text-sm rounded border border-gray-200', canForward ? 'bg-white text-gray-600 hover:bg-gray-50' : 'bg-gray-50 text-gray-300 cursor-not-allowed']">→</button>
      </div>
      <span class="text-xs text-gray-500">{{ windowLabel }}</span>
    </div>

    <!-- Zone graphe -->
    <div v-if="loading" class="py-10 text-center text-sm text-gray-400">{{ $t('commun.chargement') }}</div>

    <template v-else>
      <!-- Vue horaire (échantillons bruts) -->
      <div v-if="mode === 'intraday'">
        <div v-if="!segments.length" class="py-10 text-center text-sm text-gray-400">
          {{ $t('batterie.aucunEchantillon') }}
        </div>
        <svg v-else :viewBox="`0 0 ${W} ${H}`" class="w-full" preserveAspectRatio="xMidYMid meet"
             role="img" :aria-label="$t('batterie.ariaHoraire')">
          <!-- Seuils -->
          <g v-for="t in thresholdLines" :key="t.label">
            <line :x1="padL" :x2="W - padR" :y1="t.y" :y2="t.y" :stroke="t.color"
                  stroke-width="1" stroke-dasharray="4 3" opacity="0.7" />
            <text :x="W - padR + 3" :y="t.y + 3" :fill="t.color" font-size="10">{{ t.label }}</text>
          </g>
          <!-- Lignes (segmentées sur les trous > 3 h : évite d'interpoler le silence éco) -->
          <polyline v-for="(seg, si) in segments" :key="'seg' + si"
                    :points="seg.line" fill="none" stroke="#2563eb" stroke-width="1.6"
                    stroke-linejoin="round" stroke-linecap="round" />
          <!-- Points (si peu nombreux) avec tooltip -->
          <g v-if="showDots">
            <circle v-for="(p, i) in dots" :key="'d' + i" :cx="p.x" :cy="p.y" r="1.8" :fill="p.color">
              <title>{{ p.tip }}</title>
            </circle>
          </g>
          <!-- Axe Y -->
          <g v-for="tick in yTicks" :key="'y' + tick">
            <text :x="padL - 6" :y="yOf(tick) + 3" text-anchor="end" fill="#9ca3af" font-size="10">{{ tick }}</text>
          </g>
          <!-- Axe X -->
          <g v-for="(lbl, i) in xLabels" :key="'x' + i">
            <line :x1="lbl.x" :x2="lbl.x" :y1="H - padB" :y2="H - padB + 3" stroke="#d1d5db" stroke-width="1" />
            <text :x="lbl.x" :y="H - 6" text-anchor="middle" fill="#9ca3af" font-size="10">{{ lbl.text }}</text>
          </g>
        </svg>
        <!-- Capacité restante (cap, mA·h) — MÊME axe X, échelle propre : les deux courbes ne
             partagent pas d'unité, une superposition à double échelle donnerait des croisements
             qui ne veulent rien dire. La pente ici EST la consommation ; c'est cette droite que
             la réserve mesure. -->
        <div v-if="capSegments.length" class="mt-1">
          <svg :viewBox="`0 0 ${W} ${CH}`" class="w-full" preserveAspectRatio="xMidYMid meet"
               role="img" :aria-label="$t('batterie.ariaCapacite')">
            <line :x1="padL" :x2="W - padR" :y1="CH - capPadB" :y2="CH - capPadB"
                  stroke="#e5e7eb" stroke-width="1" />
            <polyline v-for="(seg, si) in capSegments" :key="'cap' + si"
                      :points="seg.line" fill="none" stroke="#0d9488" stroke-width="1.6"
                      stroke-linejoin="round" stroke-linecap="round" />
            <g v-if="showDots">
              <circle v-for="(p, i) in capDots" :key="'cd' + i" :cx="p.x" :cy="p.y" r="1.8" fill="#0d9488">
                <title>{{ p.tip }}</title>
              </circle>
            </g>
            <g v-for="tick in capTicks" :key="'cy' + tick.v">
              <text :x="padL - 6" :y="tick.y + 3" text-anchor="end" fill="#9ca3af" font-size="10">{{ tick.v }}</text>
            </g>
            <text :x="W - padR + 3" :y="capPadT + 8" fill="#0d9488" font-size="10">mA·h</text>
          </svg>
        </div>
        <div class="flex flex-wrap gap-3 mt-1 text-xs text-gray-500">
          <span class="inline-flex items-center gap-1"><span class="w-3 h-2 rounded-sm inline-block" style="background:#f59e0b"></span>{{ $t('batterie.solaire') }}</span>
          <span class="inline-flex items-center gap-1"><span class="w-3 h-2 rounded-sm inline-block" style="background:#3b82f6"></span>{{ $t('batterie.secteur') }}</span>
          <span class="inline-flex items-center gap-1"><span class="w-3 h-2 rounded-sm inline-block" style="background:#8b5cf6"></span>{{ $t('batterie.batterie') }}</span>
          <span v-if="capSegments.length" class="inline-flex items-center gap-1"><span class="w-3 h-2 rounded-sm inline-block" style="background:#0d9488"></span>{{ $t('batterie.capaciteRestante') }}</span>
          <span class="text-gray-400">{{ $t('batterie.legendeHeure') }}</span>
        </div>
      </div>

      <!-- Vue 30 jours : bandes min/max quotidiennes -->
      <battery-chart v-else :daily="daily" :thresholds="thresholds" />
    </template>

    <p class="text-xs text-gray-400 mt-3">
      {{ $t('batterie.aideSolaire') }}
    </p>

    <!-- Admin : après un changement de batterie. L'historique est rangé par IMEI et survit au
         re-rattachement : sans ce geste, réserve, conso et bilan mélangent deux cellules. -->
    <div v-if="reinitialisable" class="mt-4 pt-3 border-t border-gray-100 flex flex-wrap items-center gap-3">
      <button @click="reinitialiser" :disabled="reinitEnCours"
              class="px-3 py-1.5 text-sm rounded-md border border-red-300 text-red-700 hover:bg-red-50 disabled:opacity-50">
        {{ reinitEnCours ? $t('batterie.effacement') : $t('batterie.reinitialiser') }}
      </button>
      <span class="text-xs text-gray-500">{{ $t('batterie.aideReinit') }}</span>
      <span v-if="reinitMessage" class="text-xs" :class="reinitOk ? 'text-green-700' : 'text-red-600'">{{ reinitMessage }}</span>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, watch } from 'vue';
import apiService from '@/services/api.service';
import BatteryChart from '@/components/common/BatteryChart.vue';
import { useI18n } from 'vue-i18n';
import { texteErreur } from '@/i18n/serveur';
import { localeIntl, FUSEAU } from '@/i18n/dates';

const DAY = 86400;

export default {
  name: 'BatteryPanel',
  components: { BatteryChart },
  props: {
    deviceId: { type: Number, required: true },
    // Bouton « Réinitialiser l'historique » : admin seulement (la route l'est aussi).
    reinitialisable: { type: Boolean, default: false }
  },
  setup(props) {
    const { t, locale } = useI18n();
    const ranges = computed(() => [
      { d: 1, label: t('batterie.plageJour') }, { d: 3, label: t('batterie.plageJours', { n: 3 }) },
      { d: 7, label: t('batterie.plageJours', { n: 7 }) }, { d: 30, label: t('batterie.plageJours', { n: 30 }) }
    ]);
    const rangeDays = ref(1);
    const nowTs = () => Math.floor(Date.now() / 1000);
    const endTs = ref(nowTs());

    const daily = ref([]);
    const samples = ref([]);
    const health = ref(null);
    const consumption = ref(null);
    const balance = ref(null);
    const thresholds = ref({ eco: 3400, eco_exit: 3600, shutdown: 2750, full: 4100 });
    const loading = ref(true);

    const mode = computed(() => (rangeDays.value <= 7 ? 'intraday' : 'daily'));
    const fromTs = computed(() => endTs.value - rangeDays.value * DAY);
    const canForward = computed(() => endTs.value < nowTs() - 60);

    // --- Chargements ---
    const loadDaily = async () => {
      const res = await apiService.getDeviceBattery(props.deviceId, 30);
      daily.value = res.data.daily || [];
      health.value = res.data.health || null;
      consumption.value = res.data.consumption || null;
      balance.value = res.data.balance || null;
      if (res.data.thresholds) thresholds.value = res.data.thresholds;
    };
    const loadSamples = async () => {
      const res = await apiService.getDeviceBatterySamples(props.deviceId, fromTs.value, endTs.value);
      samples.value = res.data.samples || [];
      if (res.data.thresholds) thresholds.value = res.data.thresholds;
    };
    const refresh = async () => {
      loading.value = true;
      try {
        if (mode.value === 'intraday') await loadSamples();
        else if (!daily.value.length) await loadDaily();
      } catch (e) { console.error('[BATT] refresh:', e); }
      finally { loading.value = false; }
    };

    // --- Réinitialisation (admin, changement de batterie) ---
    const reinitEnCours = ref(false);
    const reinitMessage = ref('');
    const reinitOk = ref(false);
    const reinitialiser = async () => {
      if (!window.confirm(t('batterie.confirmReinit'))) return;
      reinitEnCours.value = true; reinitMessage.value = '';
      try {
        const { data } = await apiService.resetDeviceBattery(props.deviceId);
        reinitOk.value = true; reinitMessage.value = data.message;
        await refresh();
      } catch (e) {
        reinitOk.value = false; reinitMessage.value = texteErreur(e, t('batterie.effacementImpossible'));
      } finally {
        reinitEnCours.value = false;
      }
    };

    onMounted(async () => {
      loading.value = true;
      try { await loadDaily(); if (mode.value === 'intraday') await loadSamples(); }
      catch (e) { console.error('[BATT] init:', e); }
      finally { loading.value = false; }
    });

    watch([rangeDays, endTs], refresh);

    const setRange = (d) => { rangeDays.value = d; };
    const shift = (dir) => {
      const next = endTs.value + dir * rangeDays.value * DAY;
      endTs.value = Math.min(next, nowTs());
    };

    // --- Badge santé ---
    // Libellé et détail viennent du serveur, en français. En français : tels quels. Dans une
    // autre langue : traduits d'après `level`, les nombres repris du texte du serveur ; à défaut,
    // le texte français.
    const texteSante = (h) => {
      const brut = { label: h.label, detail: h.detail };
      if (locale.value === 'fr') return brut;
      const L = { dead_panel: 'DeadPanel', seasonal: 'Seasonal', eco: 'Eco', full: 'Full', ok: 'Ok' }[h.level];
      if (!L) return brut;
      const d = String(h.detail || '');
      let detail = h.detail;
      // Le serveur fournit désormais les nombres (`params`) ; le relevé dans le texte français
      // ne reste qu'en secours, pour une réponse d'avant ce changement.
      const p = h.params || {};
      if (h.level === 'dead_panel') {
        const m = p.jours != null ? [null, p.jours] : d.match(/sur (\d+) j/);
        if (m) detail = t('batterie.santeDeadPanelDetail', { jours: m[1] });
      } else if (h.level === 'seasonal') {
        const m = p.mv != null ? [null, p.mv] : d.match(/~(\d+) mV\/j/);
        const e = p.mv != null ? (p.jours != null ? [null, p.jours] : null) : d.match(/dans ~(\d+) j/);
        if (m) detail = e ? t('batterie.santeSeasonalDetailEco', { mv: m[1], jours: e[1] })
                          : t('batterie.santeSeasonalDetail', { mv: m[1] });
      } else {
        detail = t(`batterie.sante${L}Detail`);
      }
      return { label: t(`batterie.sante${L}`), detail };
    };
    const healthBadge = computed(() => {
      const h = health.value;
      if (!h || !h.label) return null;
      const cls = {
        dead_panel: 'bg-red-100 text-red-700', seasonal: 'bg-amber-100 text-amber-700',
        eco: 'bg-red-100 text-red-700', full: 'bg-green-100 text-green-700', ok: 'bg-green-100 text-green-700'
      }[h.level] || 'bg-gray-100 text-gray-600';
      const icon = { dead_panel: '🔴', seasonal: '⚠️', eco: '🔋', full: '✅', ok: '✅' }[h.level] || 'ℹ️';
      return { ...texteSante(h), cls, icon };
    });

    // --- Conso mesurée (§4.6.1) et bilan de charge (signe de dq, §4.6.2) ---
    // DEUX indicateurs, volontairement non fusionnés : la conso dit ce que la carte consomme, le
    // bilan dit si quelque chose la recharge. Un panneau mort et une nuit sont indiscernables sur
    // une mesure instantanée — seul le signe de dq sur plusieurs jours les sépare.
    //
    // ⛔ Plus aucune « autonomie en jours » ici (décision 24/08) : le chiffre ne veut pas dire
    // grand-chose côté utilisateur. La pente reste calculée comme champ de DIAGNOSTIC
    // (`diag_reserve` dans la réponse API) et ne doit pas être réaffichée comme indicateur.
    const consumptionLine = computed(() => {
      const c = consumption.value;
      if (!c) return null;
      const avecCap = c.cap != null;
      const tc = (cle, p) => t(`batterie.${cle}${avecCap ? 'Cap' : ''}`, { ...p, cap: c.cap });
      if (c.mah_per_day != null) {
        // Libellé imposé : une MESURE sur une fenêtre explicite, jamais une projection.
        return tc(c.days > 1 ? 'consoMesureePlus' : 'consoMesureeUn',
                  { mah: c.mah_per_day, fenetre: c.window_days, jours: c.days });
      }
      if (c.reason === 'on_mains') {
        return tc('consoSecteur', {});
      }
      if (c.reason === 'no_data') return null;   // fw < 66350 : pas de cap, rien à dire
      if (c.reason === 'cadence') {
        return tc('consoCadence', {});
      }
      return tc('consoEnCours', { jours: c.days, fenetre: c.window_days });
    });

    // Dérive : la carte contre SA PROPRE ligne de base. Aucune comparaison entre cartes, jamais
    // de seuil absolu — le biais de jauge par exemplaire rendrait les deux trompeurs.
    const driftBadge = computed(() => {
      const d = consumption.value && consumption.value.drift;
      if (!d || d.level === 'n/a') return null;
      if (d.level === 'drift') {
        return { cls: 'bg-red-100 text-red-700', icon: '📈', label: t('batterie.deriveLabel', { facteur: d.factor }),
                 detail: t('batterie.deriveDetail', { recent: d.recent, joursRecents: d.recent_days,
                                                      base: d.baseline, joursBase: d.base_days }) };
      }
      return { cls: 'bg-green-100 text-green-700', icon: '📉', label: t('batterie.stableLabel'),
               detail: t('batterie.stableDetail', { recent: d.recent, base: d.baseline,
                                                    joursBase: d.base_days, facteur: d.factor }) };
    });

    // Bilan de charge : partout où quelque chose est censé recharger (solaire ET secteur).
    // Sur batterie seule il n'y a rien à recharger → pas de badge.
    const balanceBadge = computed(() => {
      const b = balance.value;
      if (!b || b.level === 'n/a') return null;
      const solaire = b.regime === 'solar';
      if (b.level === 'no_charge') {
        return { cls: 'bg-red-100 text-red-700', icon: '🔴', label: t(solaire ? 'batterie.recolteNulle' : 'batterie.chargeNulle'),
                 detail: t(solaire ? 'batterie.nulleDetailSolaire' : 'batterie.nulleDetailChargeur',
                           { jours: b.days, mesures: b.samples }) };
      }
      // Tous les `dq` à zéro : ce n'est pas le panneau qu'il faut soupçonner, c'est la jauge
      // (note fw 2.10.6 : dq = 0 suit aussi une reconfiguration, et partout = jauge non configurée).
      if (b.level === 'unknown' && b.reason === 'dq_zero') {
        return { cls: 'bg-amber-100 text-amber-800', icon: '⚠️', label: t('batterie.bilanIndisponible'),
                 detail: t(solaire ? 'batterie.dqZeroSolaire' : 'batterie.dqZeroChargeur', { mesures: b.samples }) };
      }
      if (b.level === 'unknown') {
        return { cls: 'bg-gray-100 text-gray-600', icon: 'ℹ️', label: t('batterie.chargeAConfirmer'),
                 detail: t('batterie.chargeAConfirmerDetail', { jours: b.days, mesures: b.samples }) };
      }
      return { cls: 'bg-green-100 text-green-700', icon: solaire ? '☀️' : '⚡',
               label: t(solaire ? 'batterie.recolteActive' : 'batterie.chargeActive'),
               detail: t(solaire ? 'batterie.activeSolaire' : 'batterie.activeChargeur',
                         { enCharge: b.negatives, mesures: b.samples, jours: b.days }) };
    });

    // --- Géométrie graphe horaire ---
    const W = 720, H = 240, padL = 44, padR = 52, padT = 12, padB = 22;
    const yLo = 2700, yHi = 4250;
    const plotH = H - padT - padB;
    const plotW = W - padL - padR;
    const GAP_MAX = 3 * 3600; // coupe la ligne au-delà de 3 h sans point

    const yOf = (mv) => padT + (yHi - Math.max(yLo, Math.min(yHi, mv))) / (yHi - yLo) * plotH;
    const xOf = (ts) => padL + (ts - fromTs.value) / (endTs.value - fromTs.value) * plotW;
    const srcColor = (src) => ({ solar: '#f59e0b', mains: '#3b82f6', battery: '#8b5cf6' }[src] || '#9ca3af');
    const fmtTip = (ts) => new Date(ts * 1000).toLocaleString(localeIntl(), { timeZone: FUSEAU, day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });

    const segments = computed(() => {
      const s = samples.value;
      const segs = [];
      let cur = [];
      for (let i = 0; i < s.length; i++) {
        if (i > 0 && (s[i].ts - s[i - 1].ts) > GAP_MAX) { if (cur.length) segs.push(cur); cur = []; }
        cur.push(s[i]);
      }
      if (cur.length) segs.push(cur);
      return segs.map(seg => ({
        line: seg.map(p => `${xOf(p.ts).toFixed(1)},${yOf(p.bat_mv).toFixed(1)}`).join(' ')
      }));
    });

    // --- Courbe capacité restante (cap, mA·h) ---
    // Tracée SOUS la courbe mV, même axe X. Échelle Y autonome, calée sur les données : cap varie
    // de quelques mA·h sur une journée là où l'échelle absolue va jusqu'à ~3500 — sur une échelle
    // fixe la courbe serait une droite plate inutile.
    const CH = 120, capPadT = 10, capPadB = 18;
    const capPlotH = CH - capPadT - capPadB;
    // Amplitude PLANCHER : sur une carte en charge cap oscille de ±1 mA·h (quantum de la jauge).
    // Sans plancher, l'auto-échelle transformerait ce bruit en montagnes russes spectaculaires.
    const CAP_MIN_SPAN = 30;

    const capPoints = computed(() => samples.value.filter(p => p.cap != null && p.cap > 0));
    const capRange = computed(() => {
      const pts = capPoints.value;
      if (!pts.length) return null;
      let lo = Infinity, hi = -Infinity;
      for (const p of pts) { if (p.cap < lo) lo = p.cap; if (p.cap > hi) hi = p.cap; }
      const mid = (lo + hi) / 2;
      let span = Math.max(hi - lo, CAP_MIN_SPAN);
      span *= 1.15;                                   // respiration haut/bas
      return { lo: Math.round(mid - span / 2), hi: Math.round(mid + span / 2) };
    });
    const capYOf = (v) => {
      const r = capRange.value;
      if (!r) return capPadT;
      const clamped = Math.max(r.lo, Math.min(r.hi, v));
      return capPadT + (r.hi - clamped) / (r.hi - r.lo) * capPlotH;
    };
    const capSegments = computed(() => {
      const pts = capPoints.value;
      const segs = [];
      let cur = [];
      for (let i = 0; i < pts.length; i++) {
        // Même règle que la courbe mV : on ne relie pas deux points séparés par un silence, sinon
        // la droite tracée interpole une consommation qu'on n'a pas mesurée.
        if (i > 0 && (pts[i].ts - pts[i - 1].ts) > GAP_MAX) { if (cur.length) segs.push(cur); cur = []; }
        cur.push(pts[i]);
      }
      if (cur.length) segs.push(cur);
      return segs.filter(seg => seg.length > 1).map(seg => ({
        line: seg.map(p => `${xOf(p.ts).toFixed(1)},${capYOf(p.cap).toFixed(1)}`).join(' ')
      }));
    });
    const capDots = computed(() => capPoints.value.map(p => ({
      x: xOf(p.ts), y: capYOf(p.cap),
      tip: `${fmtTip(p.ts)} — ${p.cap} mA·h${p.qh != null ? ` (qh ${p.qh})` : ''}`
    })));
    const capTicks = computed(() => {
      const r = capRange.value;
      if (!r) return [];
      return [r.hi, Math.round((r.lo + r.hi) / 2), r.lo].map(v => ({ v, y: capYOf(v) }));
    });

    const showDots = computed(() => samples.value.length > 0 && samples.value.length <= 200);
    const dots = computed(() => samples.value.map(p => ({
      x: xOf(p.ts), y: yOf(p.bat_mv), color: srcColor(p.src),
      tip: `${fmtTip(p.ts)} — ${p.bat_mv} mV${p.bat_pct != null ? ` / ${p.bat_pct} %` : ''}`
    })));

    const tr = t; // `t` désigne les seuils dans le calcul ci-dessous
    const thresholdLines = computed(() => {
      const t = thresholds.value || {};
      const out = [];
      if (t.full)     out.push({ y: yOf(t.full),     color: '#16a34a', label: String(t.full) });
      if (t.eco)      out.push({ y: yOf(t.eco),      color: '#d97706', label: tr('batterie.seuilEco', { mv: t.eco }) });
      if (t.shutdown) out.push({ y: yOf(t.shutdown), color: '#dc2626', label: String(t.shutdown) });
      return out;
    });
    const yTicks = [2750, 3400, 3800, 4200];

    const xLabels = computed(() => {
      const span = endTs.value - fromTs.value;
      const hourly = span <= 26 * 3600;
      const N = 6;
      const out = [];
      for (let i = 0; i <= N; i++) {
        const ts = fromTs.value + (span * i) / N;
        const d = new Date(ts * 1000);
        const text = hourly
          ? d.toLocaleTimeString(localeIntl(), { timeZone: FUSEAU, hour: '2-digit', minute: '2-digit' })
          : d.toLocaleDateString(localeIntl(), { timeZone: FUSEAU, day: '2-digit', month: '2-digit' });
        out.push({ x: xOf(ts), text });
      }
      return out;
    });

    const windowLabel = computed(() => {
      const f = new Date(fromTs.value * 1000).toLocaleString(localeIntl(), { timeZone: FUSEAU, day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
      const t = new Date(endTs.value * 1000).toLocaleString(localeIntl(), { timeZone: FUSEAU, day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
      return `${f} → ${t}`;
    });

    return {
      ranges, rangeDays, mode, canForward, loading,
      daily, thresholds, healthBadge, balanceBadge, driftBadge, consumptionLine,
      setRange, shift, windowLabel,
      reinitialiser, reinitEnCours, reinitMessage, reinitOk,
      W, H, padL, padR, padB, yOf, yTicks, thresholdLines, xLabels, segments, showDots, dots,
      CH, capPadT, capPadB, capSegments, capDots, capTicks
    };
  }
};
</script>
